import axios from 'axios';

/**
 * Geoapify Service for address autocomplete
 * Uses Geoapify Geocoding API to get addresses by ZIP code
 */
class GeoapifyService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY || '0b5e67f15eac4c92af69064fddd28139';
    this.baseURL = 'https://api.geoapify.com/v1/geocode';
  } 

  /**
   * Search addresses by ZIP code
   * @param {String} zipCode - ZIP code to search
   * @param {String} countryCode - Optional country code (e.g., 'US')
   * @returns {Promise<Array>} - Array of address suggestions
   */
  async searchByZipCode(zipCode, countryCode = 'US') {
    if (!this.apiKey) {
      console.warn('[GeoapifyService] API key not configured');
      return [];
    }

    if (!zipCode || zipCode.trim().length < 3) {
      return [];
    }

    try {
      const response = await axios.get(`${this.baseURL}/search`, {
        params: {
          postcode: zipCode.trim(),
          country: 'US', // Force US only
          apiKey: this.apiKey,
          limit: 10, // Limit to 10 results
          format: 'json',
          filter: 'countrycode:us' // Additional filter to ensure US addresses only
        },
        timeout: 5000 // 5 second timeout
      });

      if (response.data && response.data.results) {
        // Filter results to ensure only US addresses are returned
        const usResults = response.data.results.filter(result => 
          result.country_code === 'US' || result.country === 'United States'
        );

        return usResults.map(result => ({
          formatted: result.formatted || '',
          address: result.address_line1 || result.address_line2 || '',
          city: result.city || result.municipality || '',
          state: result.state || result.state_code || '',
          zipCode: result.postcode || zipCode,
          country: 'US',
          lat: result.lat,
          lon: result.lon,
          // Full address object for reference
          raw: result
        }));
      }

      return [];
    } catch (error) {
      console.error('[GeoapifyService] Error searching addresses:', error);
      return [];
    }
  }

  /**
   * Search addresses by text query (for autocomplete)
   * @param {String} query - Search query (can be ZIP, address, city, etc.)
   * @param {String} countryCode - Optional country code (e.g., 'US')
   * @returns {Promise<Array>} - Array of address suggestions
   */
  async autocomplete(query, countryCode = 'US') {
    if (!this.apiKey) {
      console.warn('[GeoapifyService] API key not configured');
      return [];
    }

    if (!query || query.trim().length < 3) {
      return [];
    }

    try {
      const response = await axios.get(`${this.baseURL}/autocomplete`, {
        params: {
          text: query.trim(),
          country: 'US', // Force US only
          apiKey: this.apiKey,
          limit: 10,
          format: 'json',
          filter: 'countrycode:us' // Additional filter to ensure US addresses only
        },
        timeout: 5000
      });

      if (response.data && response.data.results) {
        // Filter results to ensure only US addresses are returned
        const usResults = response.data.results.filter(result => 
          result.country_code === 'US' || result.country === 'United States'
        );

        return usResults.map(result => ({
          formatted: result.formatted || '',
          address: result.address_line1 || result.address_line2 || '',
          city: result.city || result.municipality || '',
          state: result.state || result.state_code || '',
          zipCode: result.postcode || '',
          country: 'US',
          lat: result.lat,
          lon: result.lon,
          raw: result
        }));
      }

      return [];
    } catch (error) {
      console.error('[GeoapifyService] Error autocompleting addresses:', error);
      return [];
    }
  }
}

export default new GeoapifyService();
